/**
 * Manages the HUD (score, status messages, aim mode indicator, doorsteps count).
 */
export class HUD {
    /** @type {HTMLElement | null} */
    #scoreElement;

    /** @type {HTMLElement | null} */
    #doorstepsElement;

    /** @type {HTMLElement | null} */
    #statusElement;

    /** @type {HTMLElement | null} */
    #scorePopupElement;

    /** @type {{ element: HTMLDivElement, timeoutId: number }[]} */
    #scorePopups = [];

    /** @type {HTMLElement | null} */
    #aimModeElement;

    /** @type {number | null} */
    #statusTimeout = null;

    constructor() {
        this.#scoreElement = document.getElementById('score');
        this.#doorstepsElement = document.getElementById('doorstepsCount');
        this.#statusElement = document.getElementById('status');
        this.#scorePopupElement = document.getElementById('scorePopup');
        this.#aimModeElement = document.getElementById('aimMode');
    }

    /**
     * Update score display
     * @param {number} score
     */
    updateScore(score) {
        if (this.#scoreElement) {
            this.#scoreElement.textContent = `Score: ${score}`;
        }
    }

    /**
     * Update doorsteps remaining display
     * @param {number} remaining
     */
    updateDoorstepsRemaining(remaining) {
        if (this.#doorstepsElement) {
            this.#doorstepsElement.textContent = `Doorsteps remaining: ${remaining}`;
        }
    }

    /**
     * Show a temporary status message
     * @param {string} message
     * @param {number} [duration=2000] - Duration in milliseconds
     */
    showStatus(message, duration = 2000) {
        if (!this.#statusElement) return;

        if (this.#statusTimeout) {
            clearTimeout(this.#statusTimeout);
        }

        this.#statusElement.textContent = message;
        this.#statusElement.style.display = 'block';

        this.#statusTimeout = setTimeout(() => {
            if (this.#statusElement) {
                this.#statusElement.textContent = '';
                this.#statusElement.style.display = 'none';
            }
            this.#statusTimeout = null;
        }, duration);
    }

    /**
     * Show a temporary score popup message near the top-center of the screen.
     * @param {string} message
     * @param {number} [duration=1700] - Duration in milliseconds
     */
    showScorePopup(message, duration = 1700) {
        if (!this.#scorePopupElement) return;

        const popup = document.createElement('div');
        popup.className = 'scorePopupItem';
        popup.textContent = message;
        popup.style.opacity = '0';

        this.#scorePopupElement.style.display = 'block';
        this.#scorePopupElement.append(popup);

        const visibleDuration = duration * 2;
        const timeoutId = window.setTimeout(() => {
            this.#removeScorePopup(popup);
        }, visibleDuration);

        this.#scorePopups.push({ element: popup, timeoutId });
        this.#layoutScorePopups();

        requestAnimationFrame(() => {
            popup.style.opacity = '1';
        });
    }

    /**
     * Recalculate popup vertical positions so newer popups stack below older ones.
     */
    #layoutScorePopups() {
        if (!this.#scorePopupElement) {
            return;
        }

        let y = 0;
        const gap = 8;

        for (const popup of this.#scorePopups) {
            popup.element.style.transform = `translate(-50%, ${y}px)`;
            y += popup.element.offsetHeight + gap;
        }
    }

    /**
     * Remove a score popup and slide remaining popups upward.
     * @param {HTMLDivElement} popupElement
     */
    #removeScorePopup(popupElement) {
        const popupIndex = this.#scorePopups.findIndex((popup) => popup.element === popupElement);
        if (popupIndex === -1) {
            return;
        }

        const [popup] = this.#scorePopups.splice(popupIndex, 1);
        clearTimeout(popup.timeoutId);

        popup.element.style.opacity = '0';
        this.#layoutScorePopups();

        window.setTimeout(() => {
            popup.element.remove();
            if (this.#scorePopupElement && this.#scorePopups.length === 0) {
                this.#scorePopupElement.style.display = 'none';
            }
        }, 220);
    }

    /**
     * Set aim mode indicator visibility
     * @param {boolean} visible
     */
    setAimModeVisible(visible) {
        if (this.#aimModeElement) {
            this.#aimModeElement.style.display = visible ? 'block' : 'none';
        }
    }

    /**
     * Show win message
     * @param {number} finalScore
     */
    showWinMessage(finalScore) {
        setTimeout(() => {
            alert(`You win! All doorsteps have been pooped on! Final Score: ${finalScore}`);
        }, 500);
    }
}
